import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { useApi } from "../../../../../http/api";
import {
  HrailSystem,
  InsertHrailSystemBody,
  UpdateHrailSystemNewImageBody,
  UpdateHrailSystemRemoveImageBody,
} from "../../../../../http/hrail/types/systems";

import { useHrailSystemsApi } from "../../../../../http/hrail/useSystems";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import useImageUploader from "../../../../ui/ImageUploader";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import Switch from "../../../../ui/Switch";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { CustomSelect } from "../../../../ui/select/CustomSelect";

interface SystemFormBody {
  code: string;
  name: string;
  mountingContext: "floor" | "tower" | "overhead" | "wall";
  isJointHoleTemplateVisible: boolean;
  isDoubleTerminalVisible: boolean;
  order: number | null;
  visibility: VisibilityScopeValue;
}

type SelectOption = {
  id: string;
  label: string;
};

interface SystemFormProps {
  system?: HrailSystem;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClose?: () => void;
  onCreated?: (id: string) => void;
  onSaved?: () => void;
  fullWidth?: boolean;
}

const SystemForm: FC<SystemFormProps> = ({ system, isDisabled, isLoading, onClose, onCreated, onSaved, fullWidth = true }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const systemApi = useHrailSystemsApi();
  const api = useApi();

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: system?.image ?? undefined,
    isLoading: !!isLoading && !system,
    disabled: isDisabled,
  });

  const defaultValues: SystemFormBody = {
    code: system?.code ?? "",
    name: system?.name ?? "",
    mountingContext: system?.mountingContext ?? "floor",
    isJointHoleTemplateVisible: system?.isJointHoleTemplateVisible ?? false,
    isDoubleTerminalVisible: system?.isDoubleTerminalVisible ?? false,
    order: system?.order ?? 50,
    visibility: system?.visibility ?? "internal",
  };

  const mountingContextOptions: SelectOption[] = [
    { id: "floor", label: i18n.translation.common.floor },
    { id: "tower", label: i18n.translation.common.tower },
    { id: "overhead", label: i18n.translation.common.overhead },
    { id: "wall", label: i18n.translation.common.wall },
  ];

  const onSubmit = () => async (formData: SystemFormBody) => {
    setIsSubmitting(true);

    const common: Omit<InsertHrailSystemBody, "image"> = {
      code: formData.code ?? "",
      name: formData.name ?? "",
      mountingContext: formData.mountingContext,
      isJointHoleTemplateVisible: formData.isJointHoleTemplateVisible,
      isDoubleTerminalVisible: formData.isDoubleTerminalVisible,
      order: formData.order ?? 50,
      visibility: formData.visibility,
    };

    if (system) {
      const update = async (body: UpdateHrailSystemRemoveImageBody | UpdateHrailSystemNewImageBody) => {
        try {
          await systemApi.updateAdminHrailSystem(system.id, body);
          dispatch(showSuccessNotification(i18n.translation));
          onSave();
          onSaved?.();
          onClose?.();
        } catch (err) {
          dispatch(showErrorNotification(i18n, err));
        } finally {
          setIsSubmitting(false);
        }
      };

      if (newImage) {
        try {
          const signedUrl = await systemApi.generateUploadImageUrl(system.id, newImage.name, newImage.type);
          const uploadResponse = await api.uploadFile(signedUrl, newImage);
          if (!uploadResponse.ok) {
            dispatch(showErrorNotification(i18n, uploadResponse));
            setIsSubmitting(false);
            return;
          }
          await update({
            ...common,
            image: newImage.name,
          });
        } catch (err) {
          dispatch(showErrorNotification(i18n, err));
          setIsSubmitting(false);
        }
      } else {
        await update({
          ...common,
          removeImage,
        });
      }
    } else {
      try {
        const res = await systemApi.insertAdminHrailSystem({
          ...common,
          image: newImage?.name ?? null,
        });
        if (newImage) {
          const signedUrl = await systemApi.generateUploadImageUrl(res.id, newImage.name, newImage.type);
          const uploadResponse = await api.uploadFile(signedUrl, newImage);
          if (!uploadResponse.ok) {
            dispatch(showErrorNotification(i18n, uploadResponse));
            return;
          }
        }
        dispatch(showSuccessNotification(i18n.translation));
        onSave();
        onCreated?.(res.id);
        onSaved?.();
        onClose?.();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Form<SystemFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isDisabled || isLoading || isSubmitting}
      secondaryButton={
        onClose ? (
          <Button buttonProps={{ onClick: onClose }} type="secondary">
            {i18n.translation.common.cancel}
          </Button>
        ) : undefined
      }
      onSubmit={onSubmit}
      fullWidth={fullWidth}
    >
      {({ control, formState: { errors }, register, reset, setValue, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [system]);

        return (
          <FormFieldsContainer fullWidth={fullWidth} imageUploader={component}>
            <FormField label={i18n.translation.common.code} error={errors.code} isOptional={false}>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading || isSubmitting}
                  {...register("code", { required: true, minLength: 1 })}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.name} error={errors.name} isOptional={false}>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading || isSubmitting}
                  {...register("name", { required: true, minLength: 1 })}
                />
              )}
            </FormField>
            <Controller
              name="mountingContext"
              control={control}
              rules={{ required: true }}
              render={({ fieldState }) => (
                <FormField label={i18n.translation.common.mountingContext} error={fieldState.error} isOptional={false}>
                  {({ labelId, isInvalid }) => (
                    <CustomSelect<SelectOption>
                      inputId={labelId}
                      isInvalid={isInvalid}
                      isDisabled={isDisabled || isLoading || isSubmitting}
                      options={mountingContextOptions}
                      value={mountingContextOptions.find((option) => option.id === watch("mountingContext")) ?? null}
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.id}
                      onChange={(option) =>
                        setValue("mountingContext", (option?.id ?? "floor") as "floor" | "tower" | "overhead" | "wall")
                      }
                    />
                  )}
                </FormField>
              )}
            />
            <FormField label={i18n.translation.common.jointHoleTemplateVisible}>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={watch("isJointHoleTemplateVisible") === true}
                  disabled={isDisabled || isLoading || isSubmitting}
                  onChange={(checked) => setValue("isJointHoleTemplateVisible", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.doubleTerminalVisible}>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={watch("isDoubleTerminalVisible") === true}
                  disabled={isDisabled || isLoading || isSubmitting}
                  onChange={(checked) => setValue("isDoubleTerminalVisible", checked)}
                />
              )}
            </FormField>
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error} isOptional={false}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={typeof field.value === "number" ? field.value : null}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      min={0}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error}>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isDisabled || isLoading || isSubmitting}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default SystemForm;
